/**
 * Composer strip: what layers will be sent on the next request.
 * Collapsed = one glanceable line; expanded = layer manager with toggles.
 * Style & Grounded live in the StylePicker (composer toolbar) — not here.
 */
import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Layers,
  Fingerprint,
  BookOpen,
  FolderOpen,
  Brain,
  MessageSquare,
  AtSign,
  Users,
  Paperclip,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Toggle } from '@/components/ui/toggle';
import type {
  ContextPreviewItem,
  ContextPreviewLayer,
} from '@/lib/workspace/prompt';

interface ContextPreviewStripProps {
  layers: ContextPreviewLayer[];
  className?: string;
  defaultExpanded?: boolean;
  /** Toggle a whole layer (session, context, …) or nested item key. */
  onToggle?: (key: string, enabled: boolean) => void;
  disabled?: boolean;
  /** Active chat's thread instructions (drives the editor + layer row) */
  threadInstructions?: string;
  /** When provided, thread instructions can be added/edited from the strip */
  onSaveThreadInstructions?: (text: string) => void;
}

/** Always-on layers — de-emphasized in the collapsed summary when enabled. */
const BASELINE_IDS = new Set(['session', 'global']);

/** Per-layer iconography — matches the "Used" receipts and prompt-stack card. */
const LAYER_ICONS: Record<string, typeof Layers> = {
  session: Fingerprint,
  global: BookOpen,
  project_instr: FolderOpen,
  memory: Brain,
  context: Layers,
  thread: MessageSquare,
  nostr_chips: AtSign,
  mentions: Users,
  attachments: Paperclip,
};

function layerIcon(id: string): typeof Layers {
  return LAYER_ICONS[id] ?? Layers;
}

/** "Memory 2" / "Context pack 3" — compact count for the collapsed line. */
function layerSummary(l: ContextPreviewLayer): string {
  const n =
    l.items != null
      ? l.items.filter((i) => i.enabled).length
      : l.count != null && l.count > 0
        ? l.count
        : null;
  return n != null && n > 0 ? `${l.label} ${n}` : l.label;
}

function ItemRow({
  item,
  onToggle,
  disabled,
}: {
  item: ContextPreviewItem;
  onToggle?: (key: string, enabled: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <li className="flex items-center gap-2.5 py-1 min-w-0">
      {onToggle && (
        <Toggle
          checked={item.enabled}
          onChange={(on) => onToggle(item.key, on)}
          disabled={disabled}
          label={`${item.enabled ? 'Exclude' : 'Include'} ${item.label}`}
        />
      )}
      <span
        className={cn(
          'min-w-0 truncate transition-colors',
          item.enabled ? 'text-[#8a8884]' : 'text-[#4a4a4a]',
        )}
        title={item.label}
      >
        {item.label}
      </span>
      {item.detail && (
        <span className="ml-auto shrink-0 text-[10px] text-[#3a3a3a]">
          {item.detail}
        </span>
      )}
    </li>
  );
}

export function ContextPreviewStrip({
  layers,
  className,
  defaultExpanded = false,
  onToggle,
  disabled,
  threadInstructions,
  onSaveThreadInstructions,
}: ContextPreviewStripProps) {
  const [open, setOpen] = useState(defaultExpanded);
  const [editingThread, setEditingThread] = useState(false);
  const [threadDraft, setThreadDraft] = useState('');

  const hasThreadLayer = layers.some((l) => l.id === 'thread');

  const startEditThread = () => {
    setThreadDraft(threadInstructions ?? '');
    setEditingThread(true);
  };

  const saveThread = () => {
    onSaveThreadInstructions?.(threadDraft.trim());
    setEditingThread(false);
  };

  if (layers.length === 0) return null;

  const enabled = layers.filter((l) => l.enabled);
  const extras = enabled.filter((l) => !BASELINE_IDS.has(l.id));
  const omittedCount = layers.filter((l) => !l.enabled).length;

  // Collapsed line: extras first (what's unusual this turn), else quiet baseline.
  const summary = extras.length > 0 ? extras.map(layerSummary).join(' · ') : null;

  return (
    <div
      className={cn(
        'rounded-xl border border-[#1f1f1f] bg-[#111]/90 text-[11px]',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#161616] rounded-xl transition-colors"
        title="What the model will receive — expand to manage layers"
        aria-expanded={open}
      >
        <Layers className="w-3.5 h-3.5 shrink-0 text-[#4a4a4a]" />
        <span className="flex-1 min-w-0 truncate">
          {summary ? (
            <span className="text-[#8a8884]">{summary}</span>
          ) : (
            <span className="text-[#4a4a4a]">Session · Instructions</span>
          )}
          {omittedCount > 0 && (
            <span className="text-amber-500/70"> · {omittedCount} off</span>
          )}
        </span>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 shrink-0 text-[#4a4a4a]" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 shrink-0 text-[#4a4a4a]" />
        )}
      </button>

      {open && (
        <div className="px-3 pb-2.5 pt-2 border-t border-[#1f1f1f]">
          <ul className="space-y-2.5">
            {layers.map((l) => {
              const Icon = layerIcon(l.id);
              const enabledCount = l.items?.filter((i) => i.enabled).length ?? 0;
              return (
                <li key={l.id}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    {onToggle && l.toggleable !== false ? (
                      <Toggle
                        checked={l.enabled}
                        onChange={(on) => onToggle(l.id, on)}
                        disabled={disabled}
                        label={`${l.enabled ? 'Exclude' : 'Include'} ${l.label}`}
                      />
                    ) : (
                      <span className="w-9 shrink-0" aria-hidden />
                    )}
                    <Icon
                      className={cn(
                        'w-3.5 h-3.5 shrink-0 transition-colors',
                        l.enabled ? 'text-[#8a8884]' : 'text-[#3a3a3a]',
                      )}
                    />
                    <span
                      className={cn(
                        'font-medium shrink-0 transition-colors',
                        l.enabled ? 'text-[#e8e6e3]/90' : 'text-[#4a4a4a]',
                      )}
                    >
                      {l.label}
                      {l.items != null
                        ? enabledCount === l.items.length
                          ? ` ${l.items.length}`
                          : ` ${enabledCount}/${l.items.length}`
                        : l.count != null
                          ? ` ${l.count}`
                          : ''}
                    </span>
                    {l.detail && l.enabled && !l.items?.length && (
                      <span className="ml-auto text-[#4a4a4a] truncate min-w-0 pl-2">
                        {l.detail}
                      </span>
                    )}
                    {l.id === 'thread' && onSaveThreadInstructions && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditThread();
                        }}
                        className="ml-auto p-1 rounded text-[#3a3a3a] hover:text-[#e8e6e3] transition-colors shrink-0"
                        title="Edit thread instructions"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {l.items && l.items.length > 0 && (
                    <ul
                      className={cn(
                        'mt-1 ml-[46px] space-y-0.5',
                        !l.enabled && 'opacity-60',
                      )}
                    >
                      {l.items.map((item) => (
                        <ItemRow
                          key={item.key}
                          item={{
                            ...item,
                            // Parent off ⇒ show children as off (toggling one on re-enables parent)
                            enabled: l.enabled && item.enabled,
                          }}
                          onToggle={
                            onToggle && l.enabled
                              ? onToggle
                              : onToggle
                                ? (key, on) => {
                                    if (on && !l.enabled) onToggle(l.id, true);
                                    onToggle(key, on);
                                  }
                                : undefined
                          }
                          disabled={disabled}
                        />
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Thread instructions: ghost add-row, or inline editor */}
          {onSaveThreadInstructions && !hasThreadLayer && !editingThread && (
            <button
              type="button"
              onClick={startEditThread}
              disabled={disabled}
              className="mt-1.5 flex items-center gap-2.5 text-left text-[#4a4a4a] hover:text-[#8a8884] transition-colors disabled:opacity-40"
            >
              <span className="w-9 shrink-0" aria-hidden />
              <MessageSquare className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px]">Add thread instructions — this chat only</span>
            </button>
          )}
          {editingThread && (
            <div className="mt-2 rounded-lg border border-[#2a2a2a] bg-[#0a0a0a] p-2.5 space-y-2">
              <textarea
                value={threadDraft}
                onChange={(e) => setThreadDraft(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Rules for this chat only, e.g. “Answer in German · under 200 words”"
                className="w-full bg-transparent text-[12px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none resize-y leading-relaxed"
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-[#3a3a3a]">
                  Stacked last before your messages
                </span>
                <span className="flex items-center gap-1.5">
                  {!!threadInstructions?.trim() && (
                    <button
                      type="button"
                      onClick={() => {
                        onSaveThreadInstructions?.('');
                        setEditingThread(false);
                        setThreadDraft('');
                      }}
                      className="px-2 py-1 text-[11px] text-red-400/70 hover:text-red-400 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingThread(false)}
                    className="px-2 py-1 text-[11px] text-[#5a5a5a] hover:text-[#e8e6e3] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveThread}
                    disabled={!threadDraft.trim()}
                    className="px-2.5 py-1 text-[11px] font-medium bg-[#e8e6e3] text-[#0a0a0a] rounded-md disabled:opacity-30 transition-opacity"
                  >
                    Save
                  </button>
                </span>
              </div>
            </div>
          )}

          <p className="pt-2 text-[10px] text-[#3a3a3a]">
            {onToggle
              ? 'Toggles apply to this chat only · sent only to your model provider'
              : 'Sent only to the model provider you chose'}
          </p>
        </div>
      )}
    </div>
  );
}
